import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  csvDownloadURL,
  endpoints,
  type CampaignChannelResponse,
  type DeliverableItem,
} from "@/lib/api";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { CampaignPerformanceSummary } from "@/components/campaign/CampaignPerformanceSummary";
import { Field } from "@/components/ui/Field";
import {
  CampaignChannelCard,
  RecordDeliverableModal,
} from "@/components/campaign/CampaignChannelSection";
import {
  AddChannelModal,
  EditCampaignModal,
} from "@/components/campaign/CampaignModals";

// CampaignDetail — v1.2: an umbrella campaign with N KOL deals. Each
// campaign_channel becomes its own card; deliverables are grouped under
// the card whose channel published the video.

const CAMPAIGN_STATUS_VARIANT: Record<
  string,
  "success" | "info" | "secondary" | "danger" | "warning"
> = {
  planned: "info",
  active: "success",
  completed: "secondary",
  cancelled: "danger",
  archived: "secondary",
};

const CAMPAIGN_STATUS_LABEL: Record<string, string> = {
  planned: "计划中",
  active: "进行中",
  completed: "已完成",
  cancelled: "已取消",
  archived: "已归档",
};

export default function CampaignDetail() {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const id = Number(params.id);
  const [addingChannel, setAddingChannel] = useState(false);
  const [editing, setEditing] = useState(false);
  const [recordingFor, setRecordingFor] = useState<CampaignChannelResponse | null>(null);

  // v2.1.3 — archive replaces delete in the UI. status='archived' hides the
  // campaign from the default list view but keeps campaign_channels and
  // sponsorship.campaign_id intact. True DELETE is no longer reachable from
  // the UI (operators can't accidentally orphan sponsorship history); SQL
  // is the only way to actually drop the row.
  const archiveMut = useMutation({
    mutationFn: () => endpoints.updateCampaign(id, { status: "archived" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      qc.invalidateQueries({ queryKey: ["campaign", id] });
      navigate("/campaigns", { replace: true });
    },
  });

  // v1.4.5 — one-click refresh stats for all delivered videos in this
  // campaign. Saves the operator from waiting up to 6h for the next
  // video_stats_hot tick when they want fresh numbers right now.
  const refreshDeliverablesMut = useMutation({
    mutationFn: () => endpoints.refreshCampaignDeliverables(id),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["campaign-deliverables", id] });
      qc.invalidateQueries({ queryKey: ["video-milestones"] });
      alert(
        `${data.videos_refreshed} / ${data.videos_considered} 个内容已刷新（写入 ${data.snapshots_written} 条快照）${data.failed ? `，${data.failed} 失败` : ""}`,
      );
    },
    onError: (err) => alert((err as Error).message),
  });

  const campaignQ = useQuery({
    queryKey: ["campaign", id],
    queryFn: () => endpoints.campaign(id),
    enabled: Number.isFinite(id),
  });

  const deliverablesQ = useQuery({
    queryKey: ["campaign-deliverables", id],
    queryFn: () => endpoints.campaignDeliverables(id),
    enabled: Number.isFinite(id),
  });

  if (!Number.isFinite(id)) {
    return <div className="p-6 text-sm text-destructive">无效的投放 ID</div>;
  }

  const campaign = campaignQ.data;
  const channels = campaign?.channels ?? [];
  const deliverables = deliverablesQ.data ?? [];

  // Group deliverables by video_channel_id so each campaign_channel card
  // shows only its own KOL's videos.
  const deliverablesByChannel = new Map<number, DeliverableItem[]>();
  for (const d of deliverables) {
    const list = deliverablesByChannel.get(d.video_channel_id) ?? [];
    list.push(d);
    deliverablesByChannel.set(d.video_channel_id, list);
  }

  return (
    <div className="h-full overflow-y-auto bg-muted/30">
      <div className="border-b border-border bg-background px-6 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            ← 返回
          </Button>
          <h1 className="text-lg font-semibold">
            {campaign?.name ?? `投放 #${id}`}
          </h1>
          {campaign && (
            <Badge variant={CAMPAIGN_STATUS_VARIANT[campaign.status] ?? "secondary"}>
              {CAMPAIGN_STATUS_LABEL[campaign.status] ?? campaign.status}
            </Badge>
          )}
          <div className="ml-auto flex items-center gap-2">
            {campaign && (
              <Link
                to={`/campaigns/${id}/report`}
                className="inline-flex h-8 items-center justify-center rounded-md border border-primary/50 bg-primary/10 px-3 text-xs font-medium text-primary transition-colors hover:bg-primary/20"
                title="老板视角的复盘报告(可导 PDF / 复制 Markdown)"
              >
                📊 复盘报告
              </Link>
            )}
            {campaign && (campaign.deliverable_count ?? 0) > 0 && (
              <>
                <a
                  href={csvDownloadURL(`/api/campaigns/${id}/deliverables`)}
                  className="inline-flex h-8 items-center justify-center rounded-md border border-border bg-background px-3 text-xs font-medium transition-colors hover:bg-muted"
                  title="导出本投放所有交付内容(可直接发给老板/客户)"
                >
                  ↓ 导出 CSV
                </a>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (
                      confirm(
                        `立即拉取本投放 ${campaign.deliverable_count} 个交付内容的最新数据？`,
                      )
                    ) {
                      refreshDeliverablesMut.mutate();
                    }
                  }}
                  disabled={refreshDeliverablesMut.isPending}
                >
                  {refreshDeliverablesMut.isPending ? "刷新中…" : "刷新交付数据"}
                </Button>
              </>
            )}
            {campaign && (
              <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                编辑活动
              </Button>
            )}
            {campaign && campaign.status !== "archived" && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  if (
                    confirm(
                      `归档投放 "${campaign.name}"？所有数据(KOL 关联、sponsorship 历史、deliverables)全部保留;只是从默认列表隐藏,在筛选器勾"含归档"还能找回来。`,
                    )
                  ) {
                    archiveMut.mutate();
                  }
                }}
                disabled={archiveMut.isPending}
              >
                归档活动
              </Button>
            )}
            <Link
              to="/campaigns"
              className="text-xs text-muted-foreground hover:text-foreground hover:underline"
            >
              ← 投放管理
            </Link>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-5xl space-y-5 p-6">
        {campaignQ.isLoading && <p className="text-sm text-muted-foreground">加载中…</p>}
        {campaignQ.isError && (
          <p className="text-sm text-destructive">
            加载失败：{(campaignQ.error as Error).message}
          </p>
        )}

        {campaign && (
          <section className="rounded-md border border-border bg-background p-4">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm md:grid-cols-4">
              <Field label="投放 ID">#{campaign.id}</Field>
              <Field label="KOL 数">{campaign.channel_count}</Field>
              <Field label="已交付内容">{campaign.deliverable_count}</Field>
              <Field label="状态">
                <Badge variant={CAMPAIGN_STATUS_VARIANT[campaign.status] ?? "secondary"}>
                  {CAMPAIGN_STATUS_LABEL[campaign.status] ?? campaign.status}
                </Badge>
              </Field>
              <Field label="开始">{campaign.start_date ?? "—"}</Field>
              <Field label="结束">{campaign.end_date ?? "—"}</Field>
              {campaign.notes && (
                <div className="col-span-2 md:col-span-4">
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                    备注
                  </dt>
                  <dd className="whitespace-pre-wrap text-sm">{campaign.notes}</dd>
                </div>
              )}
            </dl>
          </section>
        )}

        {campaign && deliverables.length > 0 && (
          <CampaignPerformanceSummary
            channelCount={campaign.channel_count}
            deliverables={deliverables}
            channels={channels}
            spendByCurrency={campaign.total_spend_by_currency}
          />
        )}

        <section>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold">
              KOL 合约 ({channels.length})
            </h2>
            <Button size="sm" variant="outline" onClick={() => setAddingChannel(true)}>
              + 添加 KOL
            </Button>
          </div>
          {channels.length === 0 && (
            <p className="rounded-md border border-dashed border-border bg-background p-4 text-xs text-muted-foreground">
              这个投放还没有挂任何 KOL — 点"添加 KOL"开始。
            </p>
          )}
          <div className="space-y-4">
            {channels.map((cc) => (
              <CampaignChannelCard
                key={cc.id}
                cc={cc}
                deliverables={deliverablesByChannel.get(cc.channel_id) ?? []}
                deliverablesLoading={deliverablesQ.isLoading}
                onRecordDeliverable={() => setRecordingFor(cc)}
                campaignID={id}
              />
            ))}
          </div>
        </section>
      </div>

      {addingChannel && campaign && (
        <AddChannelModal
          campaignID={id}
          onClose={() => setAddingChannel(false)}
          onAdded={() => setAddingChannel(false)}
        />
      )}

      {editing && campaign && (
        <EditCampaignModal
          campaign={campaign}
          onClose={() => setEditing(false)}
          onSaved={() => setEditing(false)}
        />
      )}

      {recordingFor && (
        <RecordDeliverableModal
          campaignID={id}
          cc={recordingFor}
          onClose={() => setRecordingFor(null)}
          onSaved={() => setRecordingFor(null)}
        />
      )}
    </div>
  );
}
